import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  InputAdornment,
  Stack,
  Typography,
  TextField,
  Button,
  Grid,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import TwitterIcon from 'app/components/icons/login-page-icons/twitter-icon';
import FacebookIcon from 'app/components/icons/login-page-icons/facebook-icon';
import LinkedInIcon from 'app/components/icons/login-page-icons/linkedin-icon';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Chip from '@mui/material/Chip';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { shareActivity, shareOrganization, sharePetition } from 'app/shared/reducers/send-link-to-emails';
import { extractIdFromUrl, isValidEmail, shareOnSocialMedia } from 'app/utils/common-utils/common-utils';
import './invite-more.scss';
import { useLocation } from 'react-router-dom';
interface PostSuccessModalProps {
  fullScreen: boolean;
  handleModalClose: () => void;
  responseUrl?: string;
  pdfUrl?: string;
  open: boolean;
  onClose: () => void;
  message?: string;
}

const InviteMoreModal = ({ fullScreen, handleModalClose, responseUrl, pdfUrl, open, onClose, message }: PostSuccessModalProps) => {
  const [emailGroup, setEmailGroup] = useState('');
  const [openSnackBar, setOpenSnackBar] = React.useState(false);
  const [personName, setPersonName] = React.useState<string[]>([]);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [invalidEmail, setInvalidEmail] = React.useState<boolean>(false);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const isEmailsSend = useAppSelector(state => state.shareViaMail.success);
  const isEmailsSendSuccessMessage = useAppSelector(state => state.shareViaMail.successMessage);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const userState = useAppSelector(state => state.authentication.account);
  const [email, setEmail] = useState('');
  const isOwnMail = (userState?.email || userState?.login) === email;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const dispatch = useAppDispatch();
  const parseAndValidateEmails = (input: string): { valid: string[]; invalid: string[] } => {
    if (!input) return { valid: [], invalid: [] };
    // Split on multiple delimiters (comma, semicolon, newline, tab)
    const emails = input
      .split(/[\s,;]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const valid: string[] = [];
    const invalid: string[] = [];

    // To remove duplicates and validate
    const seen = new Set();

    emails.forEach(email => {
      if (!seen.has(email.toLowerCase())) {
        seen.add(email.toLowerCase());
        if (isValidEmail(email)) {
          valid.push(email);
        } else {
          invalid.push(email);
        }
      }
    });

    return { valid, invalid };
  };

  const handleSnackClick = () => {
    setOpenSnackBar(true);
  };

  const handleCopyButton = data => {
    navigator.clipboard.writeText(data);
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, 1500);
  };
  const handleCloseSnackBar = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackBar(false);
  };
  const handleEmailGroupChange = event => {
    setEmailGroup(event.target.value);
  };
  const handleActionClick = () => {
    const activityIds = extractIdFromUrl(responseUrl).toString();
    const activityId = activityIds.match(/\d+$/)?.[0];

    const sharePayload = { activityId, url: responseUrl, emailAddresses: personName };
    if (activityId && responseUrl && personName.length > 0) {
      dispatch(shareActivity(sharePayload));
      setShowButton(true);
    }
  };
  const handleActionPetitionClick = () => {
    const petitionIds = extractIdFromUrl(responseUrl).toString();
    const petitionId = petitionIds.match(/\d+$/)?.[0];
    const sharePayload = { petitionId, url: responseUrl, emailAddresses: personName };
    if (petitionId && responseUrl && personName.length > 0) {
      dispatch(sharePetition(sharePayload));
      setShowButton(true);
    }
  };

  const handleActionOrganizationClick = () => {
    const check = extractIdFromUrl(responseUrl).toString();
    const numericPart = check.match(/\d+/);
    const organizationId = numericPart ? numericPart[0] : null;

    const sharePayload = { organizationId, url: responseUrl, emailAddresses: personName, user: 'organization' };
    if (organizationId && responseUrl && personName.length > 0) {
      dispatch(shareOrganization(sharePayload));
      setShowButton(true);
    }
  };

  useEffect(() => {
    if (open) {
      setPersonName([]);
      setInputValue('');
      setShowButton(false);
    }
  }, [open]);
  const getStyles = (name: string) => {
    return {
      fontWeight: personName.includes(name) ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
      backgroundColor: theme.palette.mode === 'dark' ? 'primary.main' : '#F6F7F8',
    };
  };
  const handleDelete = (nameToDelete: string) => {
    setEmail('');
    const updatedNames = personName.filter(name => name !== nameToDelete);
    setPersonName(updatedNames);
  };

  const selectedChips = [
    ...personName.map(name => (
      <Grid item key={name} xs={12} lg={12}>
        <Chip label={name} onDelete={() => handleDelete(name)} style={getStyles(name)} />
      </Grid>
    )),
    ...invalidEmails.map(email => (
      <Grid item key={email} xs={12}>
        <Chip
          label={email}
          onDelete={() => setInvalidEmails(prev => prev.filter(e => e !== email))}
          style={{ backgroundColor: '#ffe6e6', color: 'red', border: '1px solid red' }}
        />
      </Grid>
    )),
  ];

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };
  const { pathname } = useLocation();
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === 'Enter' || event.key === ' ' || event.key === ',') && inputValue.trim() !== '') {
      event.preventDefault();

      const { valid, invalid } = parseAndValidateEmails(inputValue);

      if (message === 'Your organization has been created succesfully') {
        // For organization, check each valid email
        valid.forEach(email => {
          setEmail(email);
          if (!isOwnMail && !personName.includes(email)) {
            setPersonName(prev => [...prev, email]);
          }
        });
      } else {
        // For non-organization cases, add all valid emails
        const newValidEmails = valid.filter(email => !personName.includes(email));
        if (newValidEmails.length > 0) {
          setPersonName(prev => [...prev, ...newValidEmails]);
        }
      }

      // Add any invalid emails to invalidEmails array
      if (invalid.length > 0) {
        setInvalidEmails(prev => [...prev, ...invalid]);
        setInvalidEmail(true);
      }

      setInputValue('');
    }
  };
  const showSuccessMessageFor5Secs = () => {
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 2000);
  };
  useEffect(() => {
    if (isEmailsSend) {
      showSuccessMessageFor5Secs();
    }
  }, [isEmailsSend]);
  const action = (
    <>
      <Button size="small" onClick={handleCloseSnackBar}>
        Undo
      </Button>
      <IconButton size="small" aria-label="close" onClick={handleCloseSnackBar}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );
  const messgeToShare = 'Reach out to Reach Amy-';
  const handleDownloadPDF = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'downloaded_file.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '18px' },
          boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
          width: { lg: pathname === '/create-a-blog' ? 600 : 600 },
        },
      }}
    >
      <style>
        {`.css-1pojs4q-MuiPaper-root-MuiDialog-paper {border-radius: 15px !important;width: 100%;}`}
        {`.css-54y083-MuiPaper-root-MuiDialog-paper {border-radius: 15px !important;width: 100%;}`}
      </style>
      <DialogContent sx={{ px: 2 }}>
        <Stack direction="column" gap={1}>
          <Stack direction="row" alignItems={'center'} justifyContent={'flex-end'}>
            <CloseIcon onClick={handleModalClose} sx={{ cursor: 'pointer' }} />
          </Stack>
          <Stack direction="column" gap={1.7}>
            <Stack direction="column" gap={3} alignItems="center" justifyContent="center" sx={{ px: { lg: 10 } }}>
              {message && (
                <Typography
                  variant="h5"
                  sx={{ color: 'secondary.main', fontWeight: 600, textAlign: 'center', px: { lg: 3 }, fontSize: '15px' }}
                >
                  {message}
                </Typography>
              )}
            </Stack>
            <Stack direction="column" gap={2.4} px={2}>
              {pathname !== '/create-a-blog' && (
                <Stack direction="column" gap={2.4}>
                  <Typography className="mx-1" sx={{ fontWeight: 600, fontSize: '12px' }}>
                    Invite your friends through emaill.
                  </Typography>

                  {/* {personName.length === 5 && (
                    <Typography color="error" variant="caption">
                      You have reached max invites
                    </Typography>
                  )} */}

                  <div
                    className=" mx-1 "
                    style={{
                      border: '1px solid #bfbfbf',
                      borderRadius: '15px',
                      height: '80px',
                      overflowY: 'scroll',
                      scrollbarWidth: 'none' /* For Firefox */,
                      msOverflowStyle: 'none' /* For Internet Explorer and Edge */,
                    }}
                  >
                    {selectedChips.map((chip, index) => (
                      <div style={{ display: 'inline-block' }}>
                        <Grid key={index} sx={{ p: 1 }}>
                          {chip}
                        </Grid>
                      </div>
                    ))}

                    <div style={{ display: 'inline-block' }}>
                      <div style={{ display: 'flex' }}>
                        {/* {personName.length >= 0 && personName.length < 5 ? ( */}
                        <TextField
                          id="Chip"
                          fullWidth
                          size="small"
                          disabled={personName.length === 5 || showButton || isOwnMail}
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyDown={handleInputKeyDown}
                          placeholder="Enter emails"
                          className="no-outline"
                          InputProps={{
                            style: {
                              backgroundColor: 'primary.main',
                              fontSize: '14px',
                              padding: 0.5,
                              width: `${Math.max(inputValue.length * 10, 200)}px`,
                            },
                          }}
                        />
                        {/* ) : (
                          ''
                        )} */}
                      </div>
                    </div>

                    {/* <div className=" w-75 p-0" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {selectedChips.map((chip, index) => (
                          <Grid key={index} sx={{ p: 1 }}>
                            {chip}
                          </Grid>
                        ))}
                      </div>
                      <div >
                        {personName.length >= 0 && personName.length < 5 ? (
                          <TextField
                            id="Chip"
                            fullWidth
                            size="small"
                            disabled={personName.length === 5}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleInputKeyDown}
                            placeholder="Enter emails"
                            className="no-outline"
                            InputProps={{
                              style: { backgroundColor: 'primary.main', fontSize: '14px', padding: 0.5 },
                            }}
                          />
                        ) : (
                          ''
                        )}
                      </div>
                    </div>

                    <div className="w-25 d-flex flex-column p-0 justify-content-end align-items-center">
                      {message === 'Your petition was created successfully' ? (
                        <Button variant="text" sx={{ color: 'primary.light' }} onClick={handleActionPetitionClick}>
                          Send
                        </Button>
                      ) : message === 'Your petition was edited successfully' ? (
                        <Button variant="text" sx={{ color: 'primary.light' }} onClick={handleActionPetitionClick}>
                          Send
                        </Button>
                      ) : (
                        <Button variant="text" sx={{ color: 'primary.light' }} onClick={handleActionClick}>
                          Send
                        </Button>
                      )}
                    </div> */}
                  </div>
                  {personName.length >= 1 && (
                    <div className="d-flex justify-content-end">
                      {message === 'The petition has been posted successfully' ? (
                        <Button
                          variant="text"
                          sx={{
                            color: 'primary.light',
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                            '&:active': {
                              backgroundColor: 'transparent',
                            },
                            borderRadius: '30px',
                          }}
                          onClick={handleActionPetitionClick}
                          disabled={showButton || invalidEmails.length > 0 || personName.length === 0}
                        >
                          Send
                        </Button>
                      ) : message === 'Your petition was edited successfully' ? (
                        <Button
                          variant="text"
                          sx={{
                            backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
                            color: isDarkMode ? 'black' : '#ffff',
                            '&:hover': {
                              backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
                            },
                          }}
                          onClick={handleActionPetitionClick}
                          disabled={showButton || invalidEmails.length > 0 || personName.length === 0}
                        >
                          Send
                        </Button>
                      ) : message === 'Your organization has been created succesfully' ? (
                        <Button
                          variant="text"
                          sx={{
                            backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
                            color: isDarkMode ? 'black' : '#ffff',
                            '&:hover': {
                              backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
                            },
                          }}
                          onClick={handleActionOrganizationClick}
                          disabled={showButton || invalidEmails.length > 0 || personName.length === 0 || isOwnMail}
                        >
                          Send
                        </Button>
                      ) : (
                        <Button
                          variant="text"
                          sx={{
                            backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
                            color: isDarkMode ? 'black' : '#ffff',
                            '&:hover': {
                              backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
                            },
                          }}
                          onClick={handleActionClick}
                          disabled={showButton || invalidEmails.length > 0 || personName.length === 0}
                        >
                          Send
                        </Button>
                      )}
                    </div>
                  )}

                  <Grid item xs={12}>
                    {invalidEmails.length > 0 && (
                      <Typography color="error" variant="caption">
                        Invalid email: {invalidEmails.join(', ')}
                      </Typography>
                    )}
                    {isOwnMail && (
                      <Typography color="error" variant="caption">
                        Self-Invitations not allowed. Please use a different email address.
                      </Typography>
                    )}
                    {isEmailsSend && showSuccessMessage && (
                      <Typography color="success.main" variant="caption">
                        {'Emails sent successfully'}
                      </Typography>
                    )}
                  </Grid>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default InviteMoreModal;
